import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { formatDate } from "@ckgo2/shared/utils";
import { CheckCircle2, Circle, AlertTriangle, Plus, ListChecks, Loader2 } from "lucide-react";

type ReadinessState = "done" | "todo" | "warn" | "na";

interface ReadinessPoint {
  key: string;
  label: string;
  state: ReadinessState;
  summary?: string;
  provenance?: { who: string | null; when: string | null };
  taskId?: string;
}

interface ReadinessReport {
  orderId: string;
  status: string;
  points: ReadinessPoint[];
  done: number;
  total: number;
  attention: number;
}

function StateIcon({ state }: { state: ReadinessState }) {
  if (state === "done") return <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />;
  if (state === "warn") return <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />;
  if (state === "na")
    return <Circle className="h-5 w-5 shrink-0 text-gray-300 dark:text-gray-600" />;
  return <Circle className="h-5 w-5 shrink-0 text-gray-400" />;
}

/**
 * "Vyžaduje pozornost" — the order readiness checklist (vision §5.6 / B2.2). Derived
 * points (deposit/passports/insurance/services) are read-only; manual `readiness` tasks
 * can be ticked off here, and new ones added. Each done point shows "kdo/kdy".
 */
export default function ReadinessChecklist({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["order-readiness", orderId],
    queryFn: () => api.get<ApiResponse<ReadinessReport>>(`/api/admin/orders/${orderId}/readiness`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["order-readiness", orderId] });
  };

  const toggle = useMutation({
    mutationFn: ({ taskId, done }: { taskId: string; done: boolean }) =>
      api.patch(`/api/admin/tasks/${taskId}/status`, { status: done ? "done" : "open" }),
    onSuccess: invalidate,
  });

  const addTask = useMutation({
    mutationFn: (t: string) =>
      api.post("/api/admin/tasks", {
        entityType: "order",
        entityId: orderId,
        kind: "readiness",
        title: t,
      }),
    onSuccess: () => {
      setTitle("");
      setAdding(false);
      invalidate();
    },
  });

  const report = data?.data;

  return (
    <div className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <ListChecks className="h-5 w-5" /> Readiness
        </h2>
        {report && report.total > 0 && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              report.attention > 0
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
            }`}
          >
            {report.done}/{report.total} ready
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="space-y-2">
          {(report?.points ?? []).map((p) => {
            const isManual = !!p.taskId;
            return (
              <li key={p.key} className="flex items-start gap-2.5">
                {isManual ? (
                  <button
                    type="button"
                    onClick={() => toggle.mutate({ taskId: p.taskId!, done: p.state !== "done" })}
                    className="mt-0.5"
                    title={p.state === "done" ? "Mark not done" : "Mark done"}
                  >
                    <StateIcon state={p.state} />
                  </button>
                ) : (
                  <span className="mt-0.5">
                    <StateIcon state={p.state} />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm ${
                      p.state === "na"
                        ? "text-gray-400 dark:text-gray-500"
                        : "text-gray-800 dark:text-gray-200"
                    } ${p.state === "done" ? "line-through decoration-gray-300" : ""}`}
                  >
                    {p.label}
                  </p>
                  {p.summary && <p className="text-xs text-gray-500">{p.summary}</p>}
                  {p.provenance && (p.provenance.who || p.provenance.when) && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      {p.provenance.who ? `${p.provenance.who}` : "Done"}
                      {p.provenance.when ? `, ${formatDate(p.provenance.when)}` : ""}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Add a manual readiness item */}
      <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-700">
        {adding ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && title.trim()) addTask.mutate(title.trim());
                if (e.key === "Escape") setAdding(false);
              }}
              placeholder="e.g. Collect dietary preferences"
              className="input flex-1 text-sm"
            />
            <button
              onClick={() => title.trim() && addTask.mutate(title.trim())}
              disabled={addTask.isPending || !title.trim()}
              className="btn-primary btn-sm"
            >
              Add
            </button>
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <Plus className="h-4 w-4" /> Add checklist item
          </button>
        )}
      </div>
    </div>
  );
}
